import { App, AuthenticationError, AuthorizationError, Deployment,DeploymentLog, NetworkError, Subscription } from '@back4app2/sdk';
import { useOutletContext } from 'react-router-dom';
import { useState, useEffect, useReducer} from 'react';
import LogBox from '../components/LogBox';
import back4app2 from '../back4app2';
import { BACK4APP_DOT_COM_SITE_URL } from '../settings';
import { timeFromNow } from '../utils/dates';
import Timer from '../components/Timer';
import MyAppDeploymentStatus from '../components/MyAppDeploymentStatus';
import { AmplitudeEvent, trackEvent } from '../utils/amplitude';
import CancelBuildButton from '../components/CancelBuildButton';
import buildCommitAuthorsMessage from '../utils/buildCommitAuthorsMessage';
import { Eyebrow, ScreenHead, SurfaceCard } from '../components/ds';
import { BranchIcon } from '../components/ds/icons';
import Skeleton from '../components/Skeleton';
interface MyAppDeploymentsState {
  isLoading: boolean;
  loadingErrorMessage?: string;
  deployments?: Deployment[];
  selectedDeployment?: Deployment;
  isLoadingDeploymentLogs: boolean;
  deploymentLogsLoadingErrorMessage?: string;
  deploymentLogs?: DeploymentLog[];
}

const INITIAL_STATE: MyAppDeploymentsState = {
  isLoading: true,
  isLoadingDeploymentLogs: true,
};

enum MyAppDeploymentsActionType {
  RESET,
  FINISH_LOADING,
  SET_DEPLOYMENT,
  FINISH_LOADING_DEPLOYMENT_LOGS,
}

const finishLoading = (errorMessage?: string, deployments?: Deployment[]) =>
  ({
    type: MyAppDeploymentsActionType.FINISH_LOADING,
    payload: {
      errorMessage,
      deployments,
    },
  } as const);

const setSelectedDeployment = (
  errorMessage?: string,
  deployment?: Deployment
) =>
  ({
    type: MyAppDeploymentsActionType.SET_DEPLOYMENT,
    payload: {
      errorMessage,
      deployment,
    },
  } as const);

const finishLoadingDeploymentLogs = (
  errorMessage?: string,
  deploymentLogs?: DeploymentLog[]
) =>
  ({
    type: MyAppDeploymentsActionType.FINISH_LOADING_DEPLOYMENT_LOGS,
    payload: {
      errorMessage,
      deploymentLogs,
    },
  } as const);

const reset = () =>
  ({
    type: MyAppDeploymentsActionType.RESET,
  } as const);

type MyAppDeployemnstsAction =
  | ReturnType<typeof reset>
  | ReturnType<typeof finishLoading>
  | ReturnType<typeof setSelectedDeployment>
  | ReturnType<typeof finishLoadingDeploymentLogs>;

const reducer = (
  state: MyAppDeploymentsState = INITIAL_STATE,
  action: MyAppDeployemnstsAction
): MyAppDeploymentsState => {
  switch (action.type) {
    case MyAppDeploymentsActionType.RESET:
      return INITIAL_STATE;

    case MyAppDeploymentsActionType.FINISH_LOADING:
      return {
        ...state,
        isLoading: false,
        loadingErrorMessage: action.payload.errorMessage,
        deployments: action.payload.deployments,
      };
    case MyAppDeploymentsActionType.SET_DEPLOYMENT:
      return {
        ...state,
        selectedDeployment: action.payload.deployment,
        deploymentLogsLoadingErrorMessage: undefined,
        isLoadingDeploymentLogs: true,
        deploymentLogs: undefined,
      };
    case MyAppDeploymentsActionType.FINISH_LOADING_DEPLOYMENT_LOGS:
      return {
        ...state,
        isLoadingDeploymentLogs: false,
        deploymentLogsLoadingErrorMessage: action.payload.errorMessage,
        deploymentLogs: action.payload.deploymentLogs,
      };
  }
};

const MyAppDeployments = () => {
  const { app } = useOutletContext<{ app: App }>();
  const mainServiceEnvironment =
    app && app.mainService && app.mainService.mainServiceEnvironment;
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  let {
    deploymentLogsLoadingErrorMessage,
    deployments,
    selectedDeployment,
    deploymentLogs,
    isLoadingDeploymentLogs
  } = state;
  const [open, setIsOpen] = useState(false);

  useEffect(() => {
    trackEvent(AmplitudeEvent.AT_APP_DEPLOYMENT_PAGE);
  }, []);

  useEffect(() => {
    let subscription: Subscription;

    if (mainServiceEnvironment?.id) {
      subscription = back4app2.subscribeToDeployments(
        mainServiceEnvironment.id,
        (error, snapshot) => {
          if (error) {
            if (error instanceof NetworkError) {
              console.error("network error", error);
              dispatch(
                finishLoading(
                  "Network error when loading app deployments. Check your internet connection and try again."
                )
              );
            } else if (
              error instanceof AuthenticationError ||
              error instanceof AuthorizationError
            ) {
              window.location.replace(
                `${BACK4APP_DOT_COM_SITE_URL}/login?return-url=${encodeURIComponent(
                  window.location.href
                )}`
              );
            } else {
              console.error("unexpected error loading app deployments", error);
              dispatch(
                finishLoading(
                  "Unexpected error when loading app deployments. Please try again."
                )
              );
            }
          } else {
            dispatch(finishLoading(undefined, snapshot));
          }
        }
      );
    }

    return () => {
      if (subscription) {
        subscription.unsubscribe();
        dispatch(reset());
      }
    };
  }, [mainServiceEnvironment?.id]);

  useEffect(() => {
    let subscription: Subscription;

    if (selectedDeployment) {
      subscription = back4app2.subscribeToDeploymentLogs(
        selectedDeployment.id,
        (error, snapshot) => {
          if (error) {
            if (error instanceof NetworkError) {
              console.error("network error", error);
              dispatch(
                finishLoadingDeploymentLogs(
                  "Network error when loading last deployment logs. Check your internet connection and try again."
                )
              );
            } else if (
              error instanceof AuthenticationError ||
              error instanceof AuthorizationError
            ) {
              window.location.replace(
                `${BACK4APP_DOT_COM_SITE_URL}/login?return-url=${encodeURIComponent(
                  window.location.href
                )}`
              );
            } else {
              console.error(
                "unexpected error loading last deployment logs",
                error
              );
              dispatch(
                finishLoadingDeploymentLogs(
                  "Unexpected error when loading last deployment logs. Please try again."
                )
              );
            }
          } else {
            dispatch(finishLoadingDeploymentLogs(undefined, snapshot));
          }
        }
      );
    }

    return () => {
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, [selectedDeployment]);

  const handleExpand = (deployment: Deployment) => {
    if (!selectedDeployment) {
      setIsOpen(true);
      dispatch(setSelectedDeployment(undefined, deployment));
    } else if (selectedDeployment && selectedDeployment.id === deployment.id) {
      setIsOpen(false);
      dispatch(setSelectedDeployment(undefined, undefined));
    } else if (selectedDeployment && selectedDeployment.id !== deployment.id) {
      dispatch(setSelectedDeployment(undefined, deployment));
    }
  };

  return (
    <>
      <ScreenHead title="Deployments" />

      {deployments ? (
        deployments.length > 0 ? (
          <div className="flex flex-col gap-3">
            <Eyebrow>
              {`${deployments.length} deployment${deployments.length === 1 ? '' : 's'}`}
            </Eyebrow>
            {deployments
              .sort((a, b) => Number(b.createdAt) - Number(a.createdAt))
              .map((deployment) => {
                const isExpanded = open && selectedDeployment?.id === deployment.id;
                return (
                  <SurfaceCard key={deployment.id} className="overflow-hidden">
                    <button
                      type="button"
                      aria-expanded={isExpanded}
                      onClick={() => handleExpand(deployment)}
                      className="b4-focus w-full text-left p-3.5 flex flex-col gap-2.5 hover:bg-navy-600 transition-colors duration-[120ms]"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <span className="font-courrier text-ds-125 text-white truncate" title={deployment.id}>
                          {deployment.id.slice(0, 8)}
                        </span>
                        <MyAppDeploymentStatus deployment={deployment} />
                      </div>

                      {deployment.commitMessage ? (
                        <span className="text-ds-135 leading-[1.5] text-navy-100 b4-clamp-2">
                          {deployment.commitMessage}
                        </span>
                      ) : null}

                      <div className="flex items-center justify-between gap-3 border-t border-navy-500 pt-2.5">
                        <div className="flex items-center gap-2 min-w-0 font-courrier text-ds-115 text-navy-300">
                          {/* Digest instead of branch on the image route: a tag
                              moves, and this is the record of what actually ran. */}
                          {deployment.imageDigest ? (
                            <span className="truncate" title={deployment.imageDigest}>
                              {deployment.imageDigest.slice(0, 19)}…
                            </span>
                          ) : (<>
                            <BranchIcon size={12} />
                            <span className="truncate">{deployment.branchName}</span>
                          </>)}
                          <span>·</span>
                          <span className="truncate">
                            {(deployment.committedAt && timeFromNow(deployment.committedAt)) || ''}
                            {((deployment.commitAuthorLogin || deployment.commitCommitterLogin)
                              && ` ${buildCommitAuthorsMessage(deployment.commitAuthorLogin, deployment.commitCommitterLogin)}`) || ''}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 flex-none">
                          <Timer
                            realElapsedMS={(deployment.deployTask && deployment.deployTask.elapsedTimeMS) || 0}
                            hasFinished={(deployment.deployTask && deployment.deployTask.finishedAt instanceof Date) || false}
                          />
                          <CancelBuildButton deployment={deployment} />
                        </div>
                      </div>
                    </button>

                    {isExpanded && (
                      <div className="cursor-auto border-t border-navy-500" onClick={(e) => e.stopPropagation()}>
                        <LogBox
                          errorMessage={deploymentLogsLoadingErrorMessage}
                          logs={deploymentLogs}
                          isLoading={isLoadingDeploymentLogs}
                        />
                      </div>
                    )}
                  </SurfaceCard>
                );
              })}
          </div>
        ) : (
          <SurfaceCard tone="surface" className="px-4 py-10 text-center text-ds-135 text-navy-300">
            Nothing deployed yet.
          </SurfaceCard>
        )
      ) : !mainServiceEnvironment?.id ? (
        <SurfaceCard tone="surface" className="px-4 py-10 text-center text-ds-135 text-navy-300">
          Nothing deployed yet.
        </SurfaceCard>
      ) : (
        /* Was a bare line of text — a skeleton at least says how much is coming. */
        <div className="flex flex-col gap-3">
          {[0, 1, 2].map(i => (
            <SurfaceCard key={i} className="p-3.5 flex flex-col gap-2.5">
              <Skeleton className="h-4 w-24 rounded-[3px]" />
              <Skeleton className="h-4 w-2/3 rounded-[3px]" />
            </SurfaceCard>
          ))}
        </div>
      )}
    </>
  );
};

export default MyAppDeployments;
